import texts from '../en.json';
import { Image, Text } from '@ui/atoms';
import Button from '@ui/atoms/design-system/Button';
import { buttonSizes, buttonTypes } from '@ui/atoms/utils/enums';
import { useRouter } from 'next/router';
import useWindowDimensions from '@hooks/useWindowDimensions';
import useEmblaCarousel from 'embla-carousel-react';
import BlogsList from './BlogsList';
import WebinarsList from './WebinarsList';
import LearnMoreCarousel from './LearnMoreCarousel';
import { useEffect, useState } from 'react';

const LearnMore = ({ blogList, webinarsList }) => {
    const router = useRouter();
    const { width } = useWindowDimensions();
    const [loader, setLoader] = useState({
        bonds: false,
        webinars: false,
        newsletters: false,
    });
    // Separate carousel refs and controls for blogs and webinars
    const [blogsEmblaRef, blogsEmblaApi] = useEmblaCarousel({
        loop: true,
        align: width < 1280 ? 'center' : 'start',
        draggable: true,
        containScroll: 'trimSnaps',
    });

    const [webinarsEmblaRef, webinarsEmblaApi] = useEmblaCarousel({
        loop: true,
        align: width < 1280 ? 'center' : 'start',
        draggable: true,
    });

    const scrollPrevBlogs = () => blogsEmblaApi && blogsEmblaApi.scrollPrev();
    const scrollNextBlogs = () => blogsEmblaApi && blogsEmblaApi.scrollNext();

    const scrollPrevWebinars = () =>
        webinarsEmblaApi && webinarsEmblaApi.scrollPrev();
    const scrollNextWebinars = () =>
        webinarsEmblaApi && webinarsEmblaApi.scrollNext();

    const handleNavigation = (type) => {
        setLoader((prev) => ({ ...prev, [type]: true }));
        try {
            width >= 1024 ? window.open(`/${type}`) : router.push(`/${type}`);
        } catch (error) {
            console.error(`Error redirecting to ${type}`, error);
        } finally {
            setLoader((prev) => ({ ...prev, [type]: false }));
        }
    };

    return (
        <div className="flex flex-col items-center max-width-wrapper">
            <div className="flex flex-col gap-6 lg:gap-[72px] md:px-4 pt-8 pb-10 lg:py-16 lg:px-16 w-full ">
                <Text
                    className="h6-semibold md:h1-semibold text-gray-500 md:text-start text-center"
                    isInnerHtml
                    content={texts.LearnMoreAboutBonds}
                />
                <div className="flex flex-col xl:flex-row gap-4 md:gap-8">
                    <div className="flex flex-col gap-4 md:gap-6 xl:min-w-[302px] xl:max-w-[302px]">
                        <div className="flex flex-col px-4 md:px-0 gap-2 md:gap-3 items-center md:items-start xl:w-full">
                            <Text
                                content={texts.ExpertInsights}
                                className="bg-blue-gradient bg-clip-text text-transparent h4-semibold md:h3-semibold"
                            />
                            <Text
                                content={texts.BondsStrategies}
                                className="p4-regular md:p3-regular text-gray-600"
                            />
                        </div>
                        <Button
                            buttonSize={buttonSizes.LARGE}
                            buttonType={buttonTypes.SECONDARY}
                            buttonText={texts.ReadMoreBlogs}
                            onClick={() => handleNavigation('blogs')}
                            isLoading={loader.blogs}
                            className="text-primary-500 !rounded-full hidden xl:inline-flex"
                        />
                    </div>
                    <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 lg:gap-6 self-auto">
                        <BlogsList blogList={blogList} />
                    </div>
                    <div className="flex flex-col gap-6 xl:hidden">
                        <LearnMoreCarousel
                            scrollPrev={scrollPrevBlogs}
                            scrollNext={scrollNextBlogs}
                            emblaRef={blogsEmblaRef}
                            ariaLabel="Blogs Carousel"
                            content={
                                <BlogsList blogList={blogList} isCarousel />
                            }
                        />
                        <Button
                            buttonSize={buttonSizes.REGULAR}
                            buttonType={buttonTypes.SECONDARY}
                            buttonText={texts.ReadMoreBlogs}
                            onClick={() => handleNavigation('blogs')}
                            isLoading={loader.blogs}
                            className="text-primary-500 self-center !rounded-lg xl:hidden"
                        />
                    </div>
                </div>
                <div className="flex flex-col pt-[56px] sm:pt-0 xl:flex-row gap-4 md:gap-8">
                    <div className="flex flex-col gap-4 md:gap-6 xl:min-w-[302px] xl:max-w-[302px]">
                        <div className="flex flex-col gap-3 px-4 md:px-0 items-center md:items-start xl:w-full">
                            <Text
                                content={texts.InsightsOnDemand}
                                isInnerHtml
                                className="bg-blue-gradient bg-clip-text text-transparent h4-semibold md:h3-semibold"
                            />
                            <Text
                                content={texts.LatestWebinarsToSupportGoals}
                                className="p4-regular md:p3-regular text-gray-600"
                            />
                        </div>
                        <Button
                            buttonSize={buttonSizes.LARGE}
                            buttonType={buttonTypes.SECONDARY}
                            buttonText={texts.ViewMoreWebinars}
                            onClick={() => handleNavigation('webinars')}
                            isLoading={loader.webinars}
                            className="text-primary-500 !rounded-full hidden xl:inline-flex"
                        />
                    </div>
                    <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 lg:gap-6 self-auto">
                        <WebinarsList webinarsList={webinarsList} />
                    </div>
                    <div className="flex flex-col gap-6 xl:hidden">
                        <LearnMoreCarousel
                            scrollPrev={scrollPrevWebinars}
                            scrollNext={scrollNextWebinars}
                            emblaRef={webinarsEmblaRef}
                            ariaLabel="Webinars Carousel"
                            content={
                                <WebinarsList
                                    webinarsList={webinarsList}
                                    isCarousel
                                />
                            }
                        />
                        <Button
                            buttonSize={buttonSizes.REGULAR}
                            buttonType={buttonTypes.SECONDARY}
                            buttonText={texts.ViewMoreWebinars}
                            onClick={() => handleNavigation('webinars')}
                            isLoading={loader.webinars}
                            className="text-primary-500 !rounded-lg self-center xl:hidden"
                        />
                    </div>
                </div>
                <div className="flex flex-col items-start md:flex-row md:justify-between mx-4 md:mx-0 md:items-center gap-3 bg-secondary-100 rounded-lg px-3 py-4 md:p-4 mt-2 md:mt-0 max-w-[1440px]">
                    <Text
                        content={texts.NewsletterDescription}
                        className="text-gray-700 h6-medium md:h5-semibold"
                    />
                    <Button
                        buttonSize={buttonSizes.REGULAR}
                        buttonType={buttonTypes.SECONDARY}
                        buttonText={texts.CheckOutMonthlyNewsletter}
                        className="!rounded-lg md:!rounded-full text-primary-500 border-primary-500"
                        suffixComponent={
                            <Image
                                src="/images/arrow-right-2.svg"
                                alt="arrow-right"
                                className="w-4 h-4"
                            />
                        }
                        onClick={() => handleNavigation('newsletters')}
                        isLoading={loader.newsletters}
                    />
                </div>
            </div>
        </div>
    );
};

export default LearnMore;
